import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Search,
  AlertCircle,
  Package,
  CheckCircle,
  XCircle,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import apiService from '../services/apiService';
import { JobDataset } from '../services/types';
import { Breadcrumb } from '../components/Breadcrumb';
import JobOperTable from '../components/jobTracker/JobOperTable';
import JobMtlTable from '../components/jobTracker/JobMtlTable';
import JobCoPartTable from '../components/jobTracker/JobCoPartTable';
import { getCachedData, setCachedData } from '../utils/pageCache';
import { parseCalendarDate } from '../utils/calendarDate';

const JobTrackerPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const jobNumParam = searchParams.get('jobNum');

  const [searchValue, setSearchValue] = useState<string>(jobNumParam || '');
  const [jobData, setJobData] = useState<JobDataset | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isPickListLoading, setIsPickListLoading] = useState(false);
  const [drawingAvailable, setDrawingAvailable] = useState<boolean | null>(
    null
  );

  const fetchJobData = useCallback(async (jobNum: string) => {
    // Check cache first
    const cacheKey = `jobData_${jobNum}`;
    const cached = getCachedData<JobDataset>(cacheKey);
    if (cached) {
      setJobData(cached);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getJobData(jobNum);
      setJobData(data);
      // Cache the result
      setCachedData(cacheKey, data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch job data');
      setJobData(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (jobNumParam) {
      setSearchValue(jobNumParam);
      fetchJobData(jobNumParam);
    }
  }, [jobNumParam, fetchJobData]);

  const drawNum = jobData?.jobHead?.drawNum?.trim();
  useEffect(() => {
    if (!drawNum) {
      setDrawingAvailable(false);
      return;
    }

    let cancelled = false;
    setDrawingAvailable(null);

    apiService.checkDrawingAvailable(drawNum).then((available) => {
      if (!cancelled) setDrawingAvailable(available);
    });

    return () => {
      cancelled = true;
    };
  }, [drawNum]);

  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmedJobNum = searchValue.trim();
    if (trimmedJobNum) {
      setSearchParams({ jobNum: trimmedJobNum });
      fetchJobData(trimmedJobNum);
    }
  };

  // Handle downloading pick list PDF
  const handleDownloadPickList = async () => {
    if (!jobData?.jobHead) return;
    try {
      setIsPickListLoading(true);
      const { presignedUrl } = await apiService.getJobPickListPdf(
        jobData.jobHead.jobNum
      );
      window.open(presignedUrl, '_blank');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to generate pick list PDF'
      );
    } finally {
      setIsPickListLoading(false);
    }
  };

  const jobHead = jobData?.jobHead;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="w-full">
        <Breadcrumb currentPage={t('jobTracker.title')} />

        {/* Header with title and description */}
        <div className="mb-8 mt-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t('jobTracker.title')}
              </h1>
              <p className="mt-2 text-gray-600">
                {t('jobTracker.description')}
              </p>
            </div>
            {jobData && (
              <button
                onClick={handleDownloadPickList}
                disabled={isPickListLoading}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors h-10 px-4 bg-amber-600 text-white hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <FileText className="w-4 h-4" />
                {isPickListLoading
                  ? t('common.loading', 'Loading...')
                  : t('jobTracker.printPickList', 'Print Pick List')}
              </button>
            )}
          </div>
        </div>

        {/* Search Section */}
        <div className="mb-6">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('jobTracker.searchPlaceholder')}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                disabled={isLoading}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || !searchValue.trim()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors"
            >
              {isLoading ? t('jobTracker.loading') : t('common.search')}
            </button>
          </form>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              {t('jobTracker.loading')}...
            </p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="p-4 mt-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-900">
                {t('common.error')}
              </h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!jobData && !isLoading && !error && (
          <div className="text-center py-12 bg-blue-50 rounded-lg border border-blue-200">
            <Package className="w-12 h-12 text-blue-400 mx-auto mb-4" />
            <p className="text-blue-900 font-medium">
              {t('jobTracker.enterJobNumber')}
            </p>
            <p className="text-blue-700 text-sm mt-1">
              {t('jobTracker.searchInstructions')}
            </p>
          </div>
        )}

        {/* Job Data Display */}
        {jobData && jobHead && !isLoading && (
          <div className="space-y-6">
            {/* Closed Job Banner */}
            {jobHead.jobClosed && (
              <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded">
                <p className="font-medium">
                  {t('jobTracker.jobClosedWarning', 'This job is closed.')}
                </p>
              </div>
            )}

            {/* Job Header Section */}
            <div className="bg-white rounded border border-gray-300 shadow-sm">
              <div className="bg-blue-50 px-4 py-3 border-b border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-blue-900">
                      {t('jobTracker.jobHeader')}
                    </h2>
                    <p className="text-xs text-blue-700 mt-1">
                      {jobHead.partNumUM}{' '}
                      {jobHead.partNum && (
                        <>
                          (
                          <Link
                            to={`/parts/${jobHead.partNum}`}
                            className="underline hover:text-blue-900 font-medium"
                          >
                            {jobHead.partNum}
                          </Link>
                          )
                        </>
                      )}{' '}
                      - {jobHead.partDescription}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {jobHead.jobFirm && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-800 rounded-full border border-gray-300">
                        {t('jobTracker.status.firm', 'Firm')}
                      </span>
                    )}
                    {jobHead.jobEngineered && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-purple-100 text-purple-800 rounded-full border border-purple-300">
                        {t('jobTracker.status.engineered')}
                      </span>
                    )}
                    {jobHead.jobReleased && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full border border-blue-300">
                        {t('jobTracker.status.released')}
                      </span>
                    )}
                    {jobHead.jobComplete && (
                      <span
                        className="px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-800 rounded-full border border-green-300 flex items-center gap-1"
                        title={
                          jobHead.jobCompletionDate
                            ? parseCalendarDate(
                                jobHead.jobCompletionDate
                              ).toLocaleDateString()
                            : ''
                        }
                      >
                        <CheckCircle size={12} />
                        {t('jobTracker.status.complete')}
                      </span>
                    )}
                    {jobHead.jobClosed && (
                      <span
                        className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-800 rounded-full border border-red-300 flex items-center gap-1"
                        title={
                          jobHead.closedDate
                            ? parseCalendarDate(
                                jobHead.closedDate
                              ).toLocaleDateString()
                            : ''
                        }
                      >
                        <XCircle size={12} />
                        {t('jobTracker.status.closed', 'Closed')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Three-column info section */}
              <div className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* General Info - First */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h3 className="text-sm font-bold text-gray-700 uppercase tracking-tight mb-3">
                      {t('jobTracker.generalInfo')}
                    </h3>
                    <div className="space-y-2 text-xs">
                      <div>
                        <span className="text-gray-600">
                          {t('jobTracker.revision')}:
                        </span>
                        <span className="ml-1 font-medium text-gray-900">
                          {jobHead.revisionNum}
                        </span>
                      </div>
                      <div className="flex items-center flex-wrap gap-x-2">
                        <span>
                          <span className="text-gray-600">
                            {t('jobTracker.drawing')}:
                          </span>
                          <span className="ml-1 font-medium text-gray-900">
                            {jobHead.drawNum}
                          </span>
                        </span>
                        {jobHead.drawNum?.trim() && (
                          <button
                            onClick={() => {
                              const dn = jobHead.drawNum?.trim();
                              if (drawingAvailable && dn) {
                                apiService.openDrawingPdf(dn);
                              }
                            }}
                            disabled={drawingAvailable !== true}
                            className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            title={
                              drawingAvailable
                                ? t('jobTracker.drawing')
                                : t('jobTracker.drawingUnavailable')
                            }
                          >
                            {drawingAvailable === null ? (
                              <div className="w-3 h-3 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
                            ) : (
                              <FileText size={12} />
                            )}
                            {t('jobTracker.drawing')}
                            {drawingAvailable && <ExternalLink size={10} />}
                          </button>
                        )}
                      </div>
                      <div>
                        <span className="text-gray-600">
                          {t('jobTracker.createdBy')}:
                        </span>
                        <span className="ml-1 font-medium text-gray-900">
                          {jobHead.createdBy}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">
                          {t('jobTracker.createDate')}:
                        </span>
                        <span className="ml-1 font-medium text-gray-900">
                          {parseCalendarDate(
                            jobHead.createDate
                          ).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Production Info - Second */}
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-bold text-green-900 uppercase tracking-tight">
                        {t('jobTracker.productionInfo')}
                      </h3>
                      {jobData.jobProd.length > 0 &&
                        jobData.jobProd[0].orderNum && (
                          <div className="px-3 py-1 bg-blue-100 border border-blue-300 rounded-md">
                            <span className="text-xs font-semibold text-blue-900">
                              {t('jobTracker.makeToOrder')}{' '}
                              {jobData.jobProd[0].orderNum}
                            </span>
                          </div>
                        )}
                    </div>
                    <div className="space-y-2 text-xs">
                      <div>
                        <span className="text-gray-600">
                          {t('jobTracker.prodQty')}:
                        </span>
                        <span className="ml-1 font-medium text-gray-900">
                          {jobHead.prodQty} {jobHead.iUM}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">
                          {t('jobTracker.qtyCompleted')}:
                        </span>
                        <span className="ml-1 font-medium text-gray-900">
                          {jobHead.qtyCompleted} {jobHead.iUM}
                        </span>
                      </div>
                      {jobData.jobPart.length > 0 && (
                        <>
                          <div>
                            <span className="text-gray-600">
                              {t('jobTracker.receivedQty')}:
                            </span>
                            <span className="ml-1 font-medium text-gray-900">
                              {jobData.jobPart[0].receivedQty} {jobHead.iUM}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-600">
                              {t('jobTracker.shippedQty')}:
                            </span>
                            <span className="ml-1 font-medium text-gray-900">
                              {jobData.jobPart[0].shippedQty} {jobHead.iUM}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Schedule Info - Third */}
                  <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <h3 className="text-sm font-bold text-purple-900 uppercase tracking-tight mb-3">
                      {t('jobTracker.scheduleInfo')}
                    </h3>
                    <div className="space-y-2 text-xs">
                      <div>
                        <span className="text-gray-600">
                          {t('jobTracker.startDate')}:
                        </span>
                        <span className="ml-1 font-medium text-gray-900">
                          {parseCalendarDate(
                            jobHead.startDate
                          ).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                          })}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">
                          {t('jobTracker.dueDate')}:
                        </span>
                        <span className="ml-1 font-medium text-gray-900">
                          {parseCalendarDate(
                            jobHead.dueDate
                          ).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                          })}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">
                          {t('jobTracker.reqDueDate')}:
                        </span>
                        <span className="ml-1 font-medium text-gray-900">
                          {parseCalendarDate(
                            jobHead.reqDueDate
                          ).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Co-Parts Table */}
            <JobCoPartTable
              parts={jobData.jobPart}
              primaryPartNum={jobHead.partNum}
            />

            {/* Operations Table */}
            <JobOperTable operations={jobData.jobOper} />

            {/* Materials Table */}
            <JobMtlTable materials={jobData.jobMtl} />
          </div>
        )}
      </div>
    </div>
  );
};

export default JobTrackerPage;
